// Upload-folder service constants
// once we encounter > maxFiles we will stop the folder upload.
export const MAX_FILES = 20000;

// Maximum concurrent folder creation requests per depth level
export const MKDIR_CONCURRENCY = 6;

// Maximum number of concurrent uploads during folder upload
// Kept below browser's HTTP/1.1 per-domain connection limit (6) to avoid connection stalling
export const MAX_ACTIVE_UPLOADS = 5;

// Maximum concurrent uploads for mobile browsers (more conservative for memory/battery)
export const MAX_ACTIVE_UPLOADS_MOBILE = 3;

export const MAX_ACTIVE_DOWNLOADS = 1;
/*
 * max size of the upload chunk.  A 100mb file is divided into CHUNKSIZE
 * bytes when transferred to MFS.
 * - MFS has a 300mb limit of chunk size.
 *
 * When tuning this value, consider the larger chunks are generally better
 * however, it also stresses the client computer more.
 */
export const CHUNKSIZE = 100 * 1024 * 1024; // 100MB;
/*
 * the maximum file size to allow to be read into memory.  If the file
 * is larger than this value, we will read the file in chunks into memory
 * else, we'll read the whole thing.
 */
export const MAX_SIZE_READ_MEMORY = 256 * 1024 * 1024;

export const MAX_RETRY_PER_UPLOAD_HOST = 3;

/**
 * GCM encryption overhead per chunk in bytes.
 * Includes: 12-byte nonce/IV + 16-byte auth tag + 8-byte metadata = 36 bytes.
 */
export const GCM_CHUNK_OVERHEAD_BYTES = 36;

/**
 * Max file sizes used in thumbnail creation.  Note HEIC have a smaller
 * size.  Value is in bytes
 */
export const MAX_SIZE_IMG_THUMB = 20 * 1024 * 1024;
export const MAX_SIZE_HEIC_THUMB = 5 * 1024 * 1024;

// Multi-file bundling constants for uploadmultichunk optimization
// These settings control how small files are bundled into single API requests

/**
 * Maximum number of files per bundle.
 * Smaller bundles complete faster, allowing more parallel bundle processing.
 * This reduces idle time from sequential phases within each bundle.
 */
export const BUNDLE_MAX_FILES = 25;

/**
 * Files larger than this threshold are uploaded individually (not bundled).
 * Must be <= GCM_PAYLOAD_SIZE (~128KB) so each bundled file fits in a single encrypted chunk.
 * Larger files require multiple chunks, where each chunk depends on the cachekey from the
 * previous chunk's response - this sequential dependency prevents bundling.
 */
export const BUNDLE_FILE_THRESHOLD = 128 * 1024; // 128KB (single GCM chunk)

/**
 * Maximum total size of a bundle in bytes.
 * Calculated from BUNDLE_MAX_FILES × BUNDLE_FILE_THRESHOLD.
 */
export const BUNDLE_MAX_SIZE = BUNDLE_MAX_FILES * BUNDLE_FILE_THRESHOLD; // ~3.2MB

export const MULTI_CHUNK_UPLOAD_TIMEOUT_MS = 100e6;

/**
 * Maximum concurrent CP API requests (pathpreupload, webfinishbackup) across all batches.
 * This global semaphore prevents overwhelming the CP API when multiple batches run in parallel.
 * Without it, 8 batches × 25 files could fire 200 simultaneous preUpload requests.
 * Note: uploadmultichunk requests go to MFS (different server) and are not limited by this.
 */
export const MAX_CP_API_CONCURRENT = 6;
/**
 * Maximum concurrent pathlist API requests during folder enumeration.
 * Value may need tuning based on pathlist controller load.
 */
export const FOLDER_ENUMERATE_CONCURRENCY = 6;

/**
 * Maximum recursion depth when enumerating folder contents for zip downloads.
 */
// aligned with agent/backend limits
export const FOLDER_ENUMERATE_MAX_DEPTH = 100;

/**
 * Maximum concurrent file download+decrypt workers for zip downloads.
 * Desktop only — zip download is not available on mobile browsers.
 * Keep at 5 or less until HTTP/2 is in place for MFS downloads.
 */
export const ZIP_DOWNLOAD_CONCURRENCY = 5;

/**
 * Max GB held in the pre-fetch buffer (completed-but-unwritten files plus in-flight downloads)
 * before the pipeline stops starting new fetches, bounding peak footprint independently of file
 * count. In GB to match the other zip flags (e.g. maxZipSizeGb); converted to bytes at the call site.
 */
export const ZIP_DOWNLOAD_MAX_BUFFERED_GB = 2;

/**
 * Within a single file, raw decrypted chunks are folded into a disk-backed Blob once pending
 * bytes exceed this threshold, capping per-file resident RAM regardless of file size.
 */
export const ZIP_FILE_BLOB_FOLD_BYTES = 64 * 1024 * 1024; // 64 MB

export const ZIP_EXTENSION = '.zip';

export const DEFAULT_ZIP_NAME = `sync-download${ZIP_EXTENSION}`;
/**
 * Read-ahead budget for streamed zip entries. Unlike ZIP_DOWNLOAD_MAX_BUFFERED_GB — which bounds
 * disk-backed Blobs — these bytes are heap-resident, so this budget is far smaller and separate.
 */
export const ZIP_STREAM_READAHEAD_MB = 256;

export const ZIP_CHUNK_MAX_RETRIES = 2;

export const ZIP_CHUNK_RETRY_DELAY_MS = 2000;

export const ZIP_MIME_TYPE = 'application/zip';

/**
 * Download chunk retry — shared by the single-file, legacy and zip secure-download paths.
 * On a transient transport failure, a chunk is retried on the same host with exponential backoff
 * + jitter up to DOWNLOAD_CHUNK_MAX_RETRIES_PER_HOST times, then rotated to the next download host.
 * This lets a chunk survive a multi-second network blip instead of failing the whole transfer.
 */
export const DOWNLOAD_CHUNK_MAX_RETRIES_PER_HOST = 3;
export const DOWNLOAD_CHUNK_RETRY_BASE_MS = 1000;
export const DOWNLOAD_CHUNK_RETRY_MAX_MS = 8000;
export const DOWNLOAD_CHUNK_RETRY_JITTER_RATIO = 0.25;
export const DOWNLOAD_CHUNK_HOST_ROTATION_DELAY_MS = 500;

/** Fewer than the per-host budget: a chunk that burned the full 10 min rarely succeeds on a retry. */
export const DOWNLOAD_CHUNK_MAX_RETRIES_ON_TIMEOUT = 1;

/** Retries of the whole fetch + decrypt + store cycle, covering the legs transport retry can't see. */
export const DOWNLOAD_CHUNK_PROCESS_MAX_RETRIES = 3;

/**
 * Maximum total zip size (in GB) before showing a warning on Safari.
 * Safari keeps Blobs in memory — exceeding this will likely OOM.
 */
export const MAX_ZIP_SIZE_SAFARI_GB = 1.5;

/**
 * Maximum total zip size (in GB) before showing a warning on Firefox.
 * Firefox keeps Blobs in memory — exceeding this will likely OOM.
 */
export const MAX_ZIP_SIZE_FIREFOX_GB = 2;
